import { v4 as uuid } from "uuid";
type IdMap = ReadonlyMap<string, string>;

const TABLE_ROW_RESERVED_KEYS = new Set(["rowId", "isPreFilledRow", "_id"]);

const mappedId = (id: string, idMap: IdMap): string => idMap.get(id) ?? id;

const remapVariableMapper = (mapper: Record<string, string>, idMap: IdMap): Record<string, string> =>
  Object.fromEntries(Object.entries(mapper).map(([questionId, variable]) => [mappedId(questionId, idMap), variable]));

const remapTableRow = (row: unknown, idMap: IdMap): unknown => {
  if (typeof row !== "object" || row === null || Array.isArray(row)) return row;
  return Object.fromEntries(
    Object.entries(row).map(([key, value]) => [TABLE_ROW_RESERVED_KEYS.has(key) ? key : mappedId(key, idMap), value]),
  );
};

interface CloneableQuestion {
  _id?: string;
  fieldsVariableMapper?: Record<string, string>;
  selectedFields?: string[];
  [key: string]: unknown;
}

interface CloneableSection {
  _id?: string;
  questionFields?: CloneableQuestion[];
  tableDataSource?: unknown[];
  [key: string]: unknown;
}

const buildIdMaps = (sections: readonly CloneableSection[]) => {
  const sectionIds = new Map<string, string>();
  const questionIds = new Map<string, string>();
  sections.forEach((section) => {
    if (section._id) sectionIds.set(section._id, uuid());
    (section.questionFields ?? []).forEach((question) => {
      if (question._id) questionIds.set(question._id, uuid());
    });
  });
  return { sectionIds, questionIds };
};

export const cloneTemplateSections = <S extends CloneableSection>(sections: readonly S[]): S[] => {
  const { sectionIds, questionIds } = buildIdMaps(sections);
  return sections.map((section) => {
    const questionFields = section.questionFields?.map((question) => ({
      ...question,
      _id: question._id ? questionIds.get(question._id) : uuid(),
      ...(question.fieldsVariableMapper
        ? { fieldsVariableMapper: remapVariableMapper(question.fieldsVariableMapper, questionIds) }
        : {}),
      ...(question.selectedFields
        ? { selectedFields: question.selectedFields.map((id) => mappedId(id, questionIds)) }
        : {}),
    }));
    return {
      ...section,
      _id: section._id ? sectionIds.get(section._id) : uuid(),
      ...(questionFields ? { questionFields } : {}),
      ...(section.tableDataSource
        ? { tableDataSource: section.tableDataSource.map((row) => remapTableRow(row, questionIds)) }
        : {}),
    };
  });
};
